import { uniqBy } from 'lodash';

export interface DepartmentData {
  deptName: string,
  id: number,
  memberGroupType?: number,
  level: number,
  orgId: number,
  parentId: number,
  sort: number,
  userList: Array<UserOption>
}
export interface UserOption {
  accountEnabled: 0 | 1 | null,
  email: string,
  headPortrait: string,
  phone: number,
  userId: number,
  userName: string,
  projectRole: string
}

export interface PersonnelItem {
  avatar: string,
  label: string,
  value: string,
  projectRole: string,
  accountEnabled: 0 | 1 | null,
  disabled: boolean
}
export interface PersonnelGroup {
  options: Array<PersonnelItem>,
  label: string,
  value: string,
  entityId: string,
  selectedType: number
}
export interface PersonnelDataset {
  userGroups: Array<PersonnelGroup>,
  groupedUsers: Array<PersonnelItem>,
  ungroupedUsers: Array<PersonnelItem>,
  allUsers: Array<PersonnelItem>
}

export interface PersonnelSubmitFormat {
  selectedType: number,
  entityId?: string
}

export interface DefaultPersonnelDataset {
  defaultGroups: Array<PersonnelGroup>
  defaultUngrouped: Array<PersonnelItem>
  allDefaultUsers: Array<PersonnelItem>
}

export type DefaultPersonnelValue = Array<string> | string;

export interface SelectOptionScope {
  option: PersonnelItem,
  label: string,
  value: string
}

const MEMBER_SELECT_TYPE = {
  userType: 0,
  depType: 1,
  allType: 2,
  projectDepartmentType: 3
};

const PROJECT_MEMBER_GROUP_PREFIX = 'project-member-group:';

const isProjectMemberGroupValue = (value: string): boolean => value.startsWith(PROJECT_MEMBER_GROUP_PREFIX);

const getGroupValue = (id: number, selectedType: number): string => (
  selectedType === MEMBER_SELECT_TYPE.projectDepartmentType
    ? `${PROJECT_MEMBER_GROUP_PREFIX}${id}`
    : id.toString()
);

// 人员选项转化数据格式
export const transformUserOption = (list: Array<UserOption> = []): Array<PersonnelItem> => {
  return list?.map(item => ({
    avatar: item.headPortrait,
    label: item.userName,
    value: item.userId.toString(),
    projectRole: item.projectRole,
    accountEnabled: item.accountEnabled,
    disabled: item.accountEnabled === 0
  })) || [];
};

// 人员分组转化数据格式
export const transformUserGroup = (list: Array<DepartmentData> = []): Array<PersonnelGroup> => {
  return list?.map(group => {
    const selectedType = group.memberGroupType ?? MEMBER_SELECT_TYPE.depType;
    return {
      value: getGroupValue(group.id, selectedType),
      entityId: group.id.toString(),
      selectedType,
      label: group.deptName,
      options: transformUserOption(group.userList)
    };
  }) || [];
};

/* 人员数据初始化 */
export const usePersonnelData = (data: Array<DepartmentData> = []): PersonnelDataset => {
  // 人员分组列表
  const userGroups: Array<PersonnelGroup> = transformUserGroup(data?.filter(group => !!group.id) || []);
  // 已分组成员列表
  const groupedUsers = uniqBy(userGroups.map(list => list.options).flat(), 'value');
  // 未分组人员列表
  const ungroupedUsers: Array<PersonnelItem> = transformUserOption(data.find(group => !group.id)?.userList || []);
  // 成员列表整合
  const allUsers = uniqBy([...groupedUsers, ...ungroupedUsers], 'value');

  return {
    userGroups,
    groupedUsers,
    ungroupedUsers,
    allUsers
  };
};

/* 已选列表转化为提交格式数据 */
export const transformSelectedForSubmit = ({
  data = [],
  userGroups = []
}: {
  data: Array<string>
  userGroups: Array<PersonnelGroup>
}) => {
  if (data?.[0] === '*') {
    // 全选情况
    return [{ selectedType: MEMBER_SELECT_TYPE.allType }];
  }

  const groupsByValue = new Map(userGroups.map(group => [group.value, group]));
  const groups = data
    ?.filter(id => groupsByValue.has(id))
    .map(id => {
      const group = groupsByValue.get(id)!;
      return {
        entityId: group.entityId,
        selectedType: group.selectedType
      };
    });
  const unavailableProjectGroups = data
    ?.filter(id => !groupsByValue.has(id) && isProjectMemberGroupValue(id))
    .map(id => ({
      entityId: id.slice(PROJECT_MEMBER_GROUP_PREFIX.length),
      selectedType: MEMBER_SELECT_TYPE.projectDepartmentType
    }));
  const options = data
    ?.filter(id => !groupsByValue.has(id) && !isProjectMemberGroupValue(id))
    .map(id => ({
      entityId: id,
      selectedType: MEMBER_SELECT_TYPE.userType
    }));

  return [...groups, ...unavailableProjectGroups, ...options];
};


/**
 * 初始化默认成员选择列表数据
 * @param sourceIds 已选人员id列表
 * @param userGroups 人员分组列表
 * @param allUsers 所有人员选项列表
*/
export const useDefaultPersonnelData = ({
  sourceIds = [],
  userGroups = [],
  allUsers = []
}: {
  sourceIds: Array<string>,
  userGroups: Array<PersonnelGroup>
  allUsers: Array<PersonnelItem>
}): DefaultPersonnelDataset => {
  // 默认成员 - 分组列表
  const defaultGroups: Array<PersonnelGroup> = userGroups.filter(group => sourceIds.includes(group.value));
  // 默认成员 - 未分组人员列表
  const defaultUngrouped: Array<PersonnelItem> = allUsers.filter(user => sourceIds.includes(user.value));
  // 默认成员 - 已分组人员列表
  const defaultGroupedUsers = uniqBy(defaultGroups.map(list => list.options).flat(), 'value');
  // 默认成员 - 所有人员列表
  const allDefaultUsers = uniqBy([...defaultUngrouped, ...defaultGroupedUsers], 'value');

  return {
    defaultGroups,
    defaultUngrouped,
    allDefaultUsers
  };
};

// 字符串转数据
export const transformStringToArray = (
  string: string = '',
  separator: string = ','
): string[] => string?.split(separator).filter(Boolean) || [];

// 数组转字符串
export const transformArrayToString = (
  list: Array<string | number> = [],
  separator: string = ','
): string => list?.join(separator) || '';
